import { AnyAction, AsyncThunk } from '@reduxjs/toolkit';

import RequestData from '../data-types/request-data';
import RequestStatus from '../enums/request-status.enum';

interface DeafultActionParams {
  action: AnyAction;
  typeEndsWith: string;
  typeStartsWith: string;
}

interface SelectStatusTypeData {
  type: RequestData['type'];
  requestState: {
    requestIds: RequestData['id'][];
    requestsById: Record<RequestData['id'], RequestData>;
  };
}

type GenericAsyncThunk = AsyncThunk<unknown, unknown, any>;

export type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
export type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
export type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

// #region Action Status
const isDefaultFulfilledAction = (
  action: AnyAction,
  typeStartsWith: string,
  typeEndsWith: string
): action is FulfilledAction => {
  return isSelectedAction({ action, typeEndsWith, typeStartsWith });
};

const isDefaultPendingAction = (
  action: AnyAction,
  typeStartsWith: string,
  typeEndsWith: string
): action is PendingAction => {
  return isSelectedAction({ action, typeEndsWith, typeStartsWith });
};

const isDefaultRejectedAction = (
  action: AnyAction,
  typeStartsWith: string,
  typeEndsWith: string
): action is RejectedAction => {
  return isSelectedAction({ action, typeEndsWith, typeStartsWith });
};

const isSelectedAction = ({
  action,
  typeEndsWith,
  typeStartsWith,
}: DeafultActionParams): boolean => {
  const startsWith = action.type.startsWith(typeStartsWith);
  const endsWith = action.type.endsWith(typeEndsWith);

  return startsWith && endsWith;
};
// #endregion Action Status

// #region Get Action Status
const selectIsFulfilledByType = ({
  requestState,
  type,
}: SelectStatusTypeData): boolean => {
  const fulfilledType = [type, 'fulfilled'].join('/');
  const request = selectRequestByType({
    requestState,
    type: fulfilledType,
  });

  return request ? request.status === RequestStatus.Succeeded : false;
};

const selectIsRequestingByType = ({
  requestState,
  type,
}: SelectStatusTypeData): boolean => {
  const pendingType = [type, 'pending'].join('/');
  const request = selectRequestByType({
    requestState,
    type: pendingType,
  });

  return request ? request.status === RequestStatus.Loading : false;
};

const selectErrorByType = ({
  requestState,
  type,
}: SelectStatusTypeData): Record<string, any> | null => {
  const failedType = [type, 'rejected'].join('/');

  const request = selectRequestByType({
    requestState,
    type: failedType,
  });

  return request?.error || null;
};

const selectRequestByType = ({
  requestState,
  type,
}: SelectStatusTypeData): RequestData => {
  const requests = selectRequestsByType({ requestState, type });

  return requests.reverse()[0];
};

const selectRequestsByType = ({
  requestState,
  type,
}: SelectStatusTypeData): RequestData[] => {
  const requests = selectRequests(requestState);

  return requests.filter((r) => r.type === type);
};

const selectRequests = (
  requestState: SelectStatusTypeData['requestState']
): RequestData[] =>
  requestState.requestIds.map((id) => requestState.requestsById[id]);
// #endregion Get Action Status

export const DefaultActions = {
  isDefaultPendingAction,
  isDefaultRejectedAction,
  isDefaultFulfilledAction,
};

export const StatusSelectors = {
  selectErrorByType,
  selectIsFulfilledByType,
  selectIsRequestingByType,
};
